import { ApolloError, useApolloClient } from "@apollo/client";
import { Capacitor } from "@capacitor/core";
import { getDataOrNull, getPictures } from "@msys/common";
import {
  ErrorMessage,
  LabeledValue,
  LoadingSpinner,
  MenuButton,
  MenuItemWithIcon,
  Modal,
  ModalActions,
  RichTextEditor,
  TabOption,
  Tabs,
} from "@msys/ui";
import {
  Close as CloseIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  MoneyOff as MoneyOffIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { convertFromRaw, RawDraftContentState } from "draft-js";
import { Form, Formik, FormikProps } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  EnterItemProps2ValueEntry,
  ItemChildrenVisibility,
  ItemType,
  namedOperations,
  ProjectType,
} from "../../../../clients/graphqlTypes.js";
import { SelectMultipleField } from "../../../commons/form-fields/SelectMultipleField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { cleanHTML, draftStateToHtml } from "../../../utils.js";
import { CategoryInput } from "../../categories/CategoryInput.js";
import { Props2AllFragment } from "../../doc-items/properties.generated.js";
import {
  groupProperties,
  isNonComputedProp,
} from "../../doc-items/properties.js";
import { PropertyField } from "../../doc-items/PropertyField.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import {
  SaveTemplateDialog_SourceItemFragment,
  useSaveTemplateDialog_CreateTemplateFromItemMutation,
  useSaveTemplateDialog_OriginTemplateQuery,
  useSaveTemplateDialog_SourceItemQuery,
  useSaveTemplateDialog_TemplateQuery,
  useSaveTemplateDialog_UpdateTemplateFromItemMutation,
} from "./SaveTemplateDialog.generated.js";
import {
  deriveSaveTemplateProperties,
  saveTemplatePropertyEntries,
} from "./saveTemplateProperties.js";

const pathToDocumentTemplates = "/templates/documents";

/** Thrown by both mutations when publishing the new version fails validation. */
const PUBLISH_VALIDATION_ERROR_CODE = "TEMPLATE_PUBLISH_VALIDATION_ERROR";

/**
 * The options the quote editor itself offers (`ChildVisibilityIconButton`) —
 * `SHOW_ONLY_MAINPRODUCT_HIDE_PRICE` is filtered out there and here alike.
 */
const CHILDREN_VISIBILITY_OPTIONS: ItemChildrenVisibility[] = [
  "SHOW_CHILDREN",
  "SHOW_CHILDREN_HIDE_PRICE",
  "HIDE_CHILDREN",
];

/** Same icon per option as `ChildVisibilityIconButton`, so the control reads
 *  identically here and in the quote editor. */
const CHILDREN_VISIBILITY_ICONS: Record<
  ItemChildrenVisibility,
  React.ReactElement
> = {
  SHOW_CHILDREN: <VisibilityIcon />,
  SHOW_CHILDREN_HIDE_PRICE: <MoneyOffIcon />,
  SHOW_ONLY_MAINPRODUCT_HIDE_PRICE: <MoneyOffIcon />,
  HIDE_CHILDREN: <VisibilityOffIcon />,
};

type DialogTab = "preview" | "properties" | "structure";

/**
 * One Structure row. Recursive on purpose: the query's nesting stops at a fixed
 * depth, so each generated level is a slightly different type and the deepest
 * omits `children` entirely — this is the shape they all satisfy.
 */
interface StructureRowItem {
  id: string;
  title: string;
  children?: readonly StructureRowItem[];
}

/**
 * Every row the Structure panel lists — the whole sub-tree, not just the first
 * level. This is what the tab's badge and the From-line's "N items" report, so
 * the two numbers and the panel cannot disagree. Defined as "what is shown", so
 * the query's fixed nesting depth bounds it honestly rather than making it wrong.
 */
const countRows = (items: readonly StructureRowItem[]): number =>
  items.reduce((total, item) => total + 1 + countRows(item.children ?? []), 0);

/** Wireframe screen 2's Preview panel photo slot. */
const PICTURE_SLOT_HEIGHT = 110;

/**
 * A floor, not a fixed height: the panel is content-sized, so a short tab does
 * not leave a large empty box, but it never collapses below this either — which
 * keeps the loading state and the empty tabs from looking broken and bounds how
 * much the dialog can grow when the data lands. `min()` so a short viewport
 * (phone landscape) can still shrink it rather than clipping the info note.
 */
const TAB_PANEL_MIN_HEIGHT = "min(160px, 30vh)";

export type SaveTemplateDialogMode =
  | { type: "create" }
  | { type: "update"; templateDocId: string };

interface FormValues {
  title: string;
  description: string;
  /** Inherited, never chosen here — see `inheritedTemplateType`. */
  templateType: { id: string; title: string } | null;
  category: string | null;
  typeOfProject: ProjectType[];
  childrenVisibility: ItemChildrenVisibility | null;
  /**
   * One entry per property the Properties tab lists, seeded with the value shown
   * and replaced as the user edits — not only what was edited. The backend
   * applies these *after* the template type, so they land on the type's
   * properties too, and what the tab showed is what the template carries.
   */
  props2Entries: EnterItemProps2ValueEntry[];
}

interface Props {
  projectId: string | null;
  docId: string;
  /** The item whose content the tabs show. */
  sourceItemId: string;
  /** What the mutation receives — `null` saves the whole quote (its root item). */
  originItemId: string | null;
  /** Names the source in the From-line: the item's or the quote's title. */
  sourceTitle: string;
  mode: SaveTemplateDialogMode;
  handleClose: () => void;
  refetchQueries?: string[];
  onSaved?: () => void;
}

export const SaveTemplateDialog = ({
  projectId,
  docId,
  sourceItemId,
  originItemId,
  sourceTitle,
  mode,
  handleClose,
  refetchQueries,
  onSaved,
}: Props) => {
  const { t } = useTranslate([
    "QuoteItem",
    "Templates",
    "TemplatesSharing",
    "TemplateTypes",
    "Global",
  ]);

  const client = useApolloClient();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [tab, setTab] = React.useState<DialogTab>("preview");
  const [validationError, setValidationError] = React.useState<string | null>(
    null
  );

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  // Which footer action is driving the current submit: the draft path skips the
  // publish confirm, so `onSubmit` has to know which button was pressed.
  const submitActionRef = React.useRef<"draft" | "publish">("publish");

  const isUpdate = mode.type === "update";
  const formId = React.useMemo(() => uniqueId(), []);

  const sourceItemQuery = useSaveTemplateDialog_SourceItemQuery({
    client,
    variables: { projectId, docId, itemId: sourceItemId },
    fetchPolicy: "cache-and-network",
  });
  const sourceItem =
    sourceItemQuery.data?.item.__typename === "Item"
      ? sourceItemQuery.data.item
      : undefined;

  const targetTemplateQuery = useSaveTemplateDialog_TemplateQuery({
    client,
    variables: { id: isUpdate ? mode.templateDocId : "" },
    skip: !isUpdate,
    fetchPolicy: "cache-and-network",
  });
  const targetTemplate = getDataOrNull(
    targetTemplateQuery.data?.quoteTemplateLatest
  )?.quoteTemplate;

  // Create mode only: an item that came from a template already carries a
  // classification, so seed the form from that template rather than making the
  // user retype what the system knows. Update mode reads the target instead.
  const originTemplateId = isUpdate
    ? null
    : (sourceItem?.templateOriginQuoteId ?? null);
  const originTemplateQuery = useSaveTemplateDialog_OriginTemplateQuery({
    client,
    variables: { id: originTemplateId ?? "" },
    skip: !originTemplateId,
    fetchPolicy: "cache-and-network",
    // The user asked to open the dialog, not to fetch the origin; the prefill is
    // best-effort, so an unresolvable origin must not raise the error link's
    // toast on top of the dialog it already degrades gracefully in.
    context: { noErrorNotification: true },
  });
  const originTemplate = getDataOrNull(
    originTemplateQuery.data?.quoteTemplateLatestAvailableVersion
  )?.quoteTemplate;

  const [createTemplateFromItem] =
    useSaveTemplateDialog_CreateTemplateFromItemMutation({ client });
  const [updateTemplateFromItem] =
    useSaveTemplateDialog_UpdateTemplateFromItemMutation({ client });

  const { projectTypeOptions } = useProjectTypes();

  // The origin-template lookup can only start once the source item has landed,
  // so `loading` alone leaves one frame where the prefill is neither in flight
  // nor arrived. Treating "an origin id with no result yet" as loading closes
  // it, and an inaccessible/deleted origin still settles via `error`.
  const isPrefillPending =
    !!originTemplateId &&
    !originTemplateQuery.data &&
    !originTemplateQuery.error;

  const isLoading =
    sourceItemQuery.loading ||
    (isUpdate && targetTemplateQuery.loading) ||
    isPrefillPending;

  /**
   * The data the form is *made of*, as opposed to the origin prefill, which is
   * best-effort. A settled query with no data counts as unavailable alongside an
   * errored one — a denied `Protected__` branch reports neither `loading` nor
   * `error` — because `loading` alone going false would hand the user live fields
   * over a blank form. In update mode submitting that form applies `values`
   * wholesale and would blank the target template's type, category, project
   * types, description and visibility on the strength of one failed read.
   *
   * The origin template is deliberately not here: it seeds nothing the user
   * cannot type, so an unresolvable origin degrades rather than blocks.
   */
  const isSourceItemUnavailable =
    !!sourceItemQuery.error || (!sourceItemQuery.loading && !sourceItem);
  const isTargetTemplateUnavailable =
    isUpdate &&
    (!!targetTemplateQuery.error ||
      (!targetTemplateQuery.loading && !targetTemplate));
  const hasUnavailableData =
    isSourceItemUnavailable || isTargetTemplateUnavailable;

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .label(t("Title", { ns: "Global" }))
      .required(),
    description: Yup.string().label(t("Description", { ns: "QuoteItem" })),
  });

  // Update mode prefills from the *target template*, not the quote item —
  // otherwise every update silently renames the template after the quote item
  // was retitled. Create mode takes name/description/visibility from the source
  // item and the classification from whatever already knows it (below).
  const metadataTemplate = isUpdate ? targetTemplate : originTemplate;

  /**
   * The template type the created template will implement — inherited, not
   * chosen: a placeholder states its type outright, anything else takes the type
   * of the template it was built from (update mode: of the template being
   * updated). `null` means the template carries no type, which is why the field
   * is not rendered at all rather than shown empty.
   */
  const inheritedTemplateType =
    (isUpdate ? null : sourceItem?.placeholderForTemplateType?.templateType) ??
    metadataTemplate?.implementsTemplateType?.templateType ??
    null;

  /**
   * The definitions the Properties tab is driven by, or `undefined` when it falls
   * back to the source item's own properties.
   *
   * Create mode only. Update mode's target template brings its own type and its
   * own question about what an update does to a template's property values; until
   * that is worked through it keeps showing the source item's properties, exactly
   * as before — which is also why the tab's empty state has to distinguish the two
   * cases by *this*, not by whether a type exists.
   */
  const templateTypeProps2 = isUpdate
    ? undefined
    : inheritedTemplateType?.props2;

  /**
   * What the Properties tab lists — the inherited type's properties with values
   * prefilled from the item, then the item's own uncovered ones.
   */
  const displayedProperties = React.useMemo(
    () =>
      deriveSaveTemplateProperties({
        templateTypeProps2,
        itemProps2: sourceItem?.props2 ?? [],
      }),
    [templateTypeProps2, sourceItem?.props2]
  );

  const initialValues: FormValues = React.useMemo(
    () => ({
      title: isUpdate
        ? (targetTemplate?.title ?? "")
        : (sourceItem?.title ?? sourceTitle),
      description: isUpdate
        ? (targetTemplate?.rootItem?.description ?? "")
        : (sourceItem?.description ?? ""),
      templateType: inheritedTemplateType
        ? {
            id: inheritedTemplateType.id,
            title: inheritedTemplateType.title,
          }
        : null,
      category: metadataTemplate?.category?.id ?? null,
      typeOfProject: metadataTemplate?.typeOfProject ?? [],
      childrenVisibility: isUpdate
        ? (targetTemplate?.rootItem?.childrenVisibility ?? null)
        : (sourceItem?.childrenVisibility ?? null),
      // Seeded, not empty: every value the tab shows is submitted, so a prefill
      // the user never touched still reaches the template. Re-seeded when the
      // queries land, which is what `enableReinitialize` is here for; once they
      // have, `initialValues` stops changing and edits are safe.
      props2Entries: saveTemplatePropertyEntries(displayedProperties),
    }),
    [
      displayedProperties,
      inheritedTemplateType,
      isUpdate,
      metadataTemplate,
      sourceItem,
      sourceTitle,
      targetTemplate,
    ]
  );

  const openSavedTemplate = (templateId: string) => {
    // The editable editor — a bare `documents/<id>` is not a mounted route, and
    // the versioned route is read-only, wrong for a draft and for "clean it up
    // later" alike.
    const path = `${pathToDocumentTemplates}/${templateId}/latest`;

    if (Capacitor.isNativePlatform()) {
      // Tapping Open is an explicit user act, so in-app navigation is allowed —
      // a new tab would either fail silently or escape the webview, leaving the
      // toast as a dead end.
      navigate(path);
      return;
    }

    window.open(path, "_blank");
  };

  /**
   * Wireframe screen 4: a bold headline, a sentence saying where the template
   * now lives, and an Open action. The wireframe's separate `Cancel` button is
   * dropped — `SnackbarProvider` already supplies a ✕ as its *default* `action`,
   * and passing our own action replaces it, so the ✕ has to be re-added here or
   * a toast with an Open button silently loses its dismiss control.
   */
  const notifySaved = (templateId: string, headline: string, body?: string) => {
    enqueueSnackbar(
      <Stack direction="column" spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {headline}
        </Typography>
        {body && <Typography variant="body2">{body}</Typography>}
      </Stack>,
      {
        variant: "success",
        // Applies to all three outcomes, draft included. Open is the only
        // direct route to the template just saved, and the provider's 5s
        // default is not long enough to read the toast and reach it — least of
        // all on the draft path, where "save as draft and clean it up later" is
        // the flow the info note promises, so Open *is* the next step. The
        // provider stretches only `variant: "error"`, so success would keep the
        // 5s. Same call `useTemplateVariantUpdateProtocol` makes for its
        // actionable toast; safe because Open and the ✕ both close it.
        persist: true,
        action: key => (
          <>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => {
                closeSnackbar(key);
                openSavedTemplate(templateId);
              }}
            >
              {t("Open", { ns: "Global" })}
            </Button>
            <IconButton
              size="small"
              onClick={() => closeSnackbar(key)}
              aria-label={t("Close", { ns: "Global" })}
            >
              <CloseIcon sx={{ color: "common.white" }} />
            </IconButton>
          </>
        ),
      }
    );
  };

  const publishedToastBody = t(
    'The template has been successfully published and can be accessed via "My Templates".',
    { ns: "TemplatesSharing" }
  );

  const handleFailure = (error: unknown) => {
    if (error instanceof ApolloError) {
      const code = error.graphQLErrors[0]?.extensions?.["code"];
      if (code === PUBLISH_VALIDATION_ERROR_CODE) {
        setValidationError(error.message);
        enqueueSnackbar(
          isUpdate
            ? t("Publishing failed — the template is unchanged", {
                ns: "TemplatesSharing",
              })
            : t("Publishing failed — nothing was saved", {
                ns: "TemplatesSharing",
              }),
          { variant: "error" }
        );
        return;
      }
    }

    // Anything that is not an `Error` still has to say something: silently
    // closing the spinner would read as a save that worked.
    enqueueSnackbar(
      error instanceof Error
        ? error.message
        : t("Something went wrong. Please try again.", { ns: "Global" }),
      { variant: "error" }
    );
  };

  // `templateTypeId` is sent explicitly, never omitted: the backend's
  // omitted-field fallback only covers a placeholder's type, so omitting it
  // would silently drop the type inherited from an origin template. No strategy
  // is sent — the type cannot be changed here, so the backend's default applies.
  const toValuesInput = (values: FormValues) => ({
    title: values.title,
    description: values.description,
    templateTypeId: values.templateType?.id ?? null,
    categoryId: values.category,
    typeOfProject: values.typeOfProject,
    // `null` is "leave it alone" on the way in, which is the only safe thing to
    // send for an item type the aggregate refuses to set a visibility on.
    childrenVisibility: canCarryChildrenVisibility(sourceItem)
      ? values.childrenVisibility
      : null,
    props2Entries: values.props2Entries,
  });

  /**
   * The refetches the mutations' consumers need, run *after* the commit rather
   * than through `awaitRefetchQueries`. Apollo folds an awaited refetch's
   * rejection into the mutation's own (`Promise.all` in `markMutationResult`),
   * so a refresh that fails once the template exists would reach
   * `handleFailure` — which says nothing was saved and leaves the form
   * retryable, and the retry creates a second template or publishes another
   * version. A stale list is the lesser failure, so it is swallowed here.
   */
  const refreshConsumers = async () => {
    try {
      await client.refetchQueries({
        include: [
          namedOperations.Query.TemplateQuotes,
          ...(refetchQueries ?? []),
        ],
      });
    } catch {
      // Nothing to surface: the save itself went through.
    }
  };

  const save = async (values: FormValues, publish: boolean) => {
    setValidationError(null);

    const mutationOptions = {
      // The dialog surfaces failures itself; without this Apollo's error link
      // raises its own generic toast on top of the inline error.
      context: { noErrorNotification: true },
    };

    try {
      if (isUpdate) {
        const response = await updateTemplateFromItem({
          variables: {
            input: {
              originProjectId: projectId,
              originDocId: docId,
              originItemId: sourceItemId,
              templateDocId: mode.templateDocId,
              values: toValuesInput(values),
            },
          },
          ...mutationOptions,
        });

        await refreshConsumers();

        const template = response.data?.updateTemplateFromItem.templateDoc;
        if (template)
          notifySaved(
            template.id,
            t("New version of template was published", {
              ns: "TemplatesSharing",
            }),
            publishedToastBody
          );
      } else {
        const response = await createTemplateFromItem({
          variables: {
            input: {
              originProjectId: projectId,
              originDocId: docId,
              originItemId,
              values: { ...toValuesInput(values), publish },
            },
          },
          ...mutationOptions,
        });

        await refreshConsumers();

        const template = response.data?.createTemplateFromItem.templateDoc;
        if (template)
          // The draft path keeps a single line: the wireframe's body sentence is
          // about *publishing*, and a draft is not available to the org yet.
          notifySaved(
            template.id,
            publish
              ? t("Template published!", { ns: "TemplatesSharing" })
              : t("Template saved as draft", { ns: "Templates" }),
            publish ? publishedToastBody : undefined
          );
      }

      onSaved?.();
      handleClose();
    } catch (error) {
      handleFailure(error);
    }
  };

  const publishConsequenceText = t(
    "Publishing makes this template available to your organisation. Only continue if client-specific details have been removed.",
    { ns: "TemplatesSharing" }
  );

  // Wireframe screen 3. Cancel returns to the dialog with every value intact —
  // structural, since the dialog stays mounted underneath the confirm.
  const confirmPublish = async () =>
    (await confirmProcessRef.current?.startConfirmProcess({
      title: t("Confirm publishing", { ns: "TemplatesSharing" }),
      text: publishConsequenceText,
      confirmButtonLabel: t("Publish", { ns: "TemplatesSharing" }),
      // "Back", not "Cancel": the dialog stays mounted underneath with every
      // value intact, so this returns to it rather than abandoning the save.
      cancelButtonLabel: t("Back", { ns: "Global" }),
    })) === true;

  const confirmUpdate = async () => {
    const consequences = [
      t(
        "All of this template's items are replaced with the content of this section.",
        { ns: "Templates" }
      ),
      ...(targetTemplate?.containsUnpublishedChanged
        ? [
            t("This template's unpublished draft changes will be discarded.", {
              ns: "Templates",
            }),
          ]
        : []),
      t("Subscribers' customisations may need review after the update.", {
        ns: "Templates",
      }),
      t("The previous published version remains available as a snapshot.", {
        ns: "Templates",
      }),
      publishConsequenceText,
    ].join("\n\n");

    return (
      (await confirmProcessRef.current?.startConfirmProcess({
        title: t('Update quote template "{name}"?', {
          ns: "Templates",
          name: targetTemplate?.title ?? "",
        }),
        text: consequences,
        confirmButtonLabel: t("Update and publish", { ns: "Templates" }),
        // "Back", not "Cancel": the dialog stays mounted underneath with every
        // value intact, so this returns to it rather than abandoning the save.
        cancelButtonLabel: t("Back", { ns: "Global" }),
      })) === true
    );
  };

  const tabOptions: TabOption<DialogTab>[] = [
    { value: "preview", label: t("Preview", { ns: "Global" }) },
    {
      value: "properties",
      // The badge counts what the panel lists, which is the properties the
      // template will carry — not the source item's, which a placeholder has
      // none of.
      label: t("Properties", { ns: "QuoteItem" }),
      count: sourceItem ? displayedProperties.length : undefined,
    },
    {
      value: "structure",
      label: t("Structure", { ns: "Templates" }),
      count: sourceItem ? countRows(sourceItem.children) : undefined,
    },
  ];

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      // Without this, Formik's `errors` stay empty until the first change, so
      // `isValid` is true on mount and the save actions are clickable with an
      // empty name — the established fix across the app's other Formik modals.
      validateOnMount
      onSubmit={async values => {
        if (submitActionRef.current === "draft") {
          await save(values, false);
          return;
        }

        const confirmed = isUpdate
          ? await confirmUpdate()
          : await confirmPublish();
        // Back leaves the dialog mounted with every entered value intact.
        if (!confirmed) return;

        await save(values, true);
      }}
    >
      {(formikProps: FormikProps<FormValues>) => {
        const isSubmitting = formikProps.isSubmitting;
        // While the source item (and, in update mode, the target template) are
        // still loading the fields are empty and the prefill hasn't landed, so
        // saving now would persist nothing of what the user is about to see.
        const cannotSave =
          isSubmitting ||
          isLoading ||
          hasUnavailableData ||
          !formikProps.isValid;
        // The structure is on screen before the data is, so the fields have to be
        // held inert until then: `enableReinitialize` would otherwise discard
        // anything typed the moment the prefill arrives. Data that failed to load
        // never arrives, so the fields stay inert rather than inviting a title
        // that would make the blank form submittable.
        const fieldsDisabled = isSubmitting || isLoading || hasUnavailableData;

        const submitWith = (action: "draft" | "publish") => async () => {
          submitActionRef.current = action;
          await formikProps.submitForm();
        };

        // `ModalActions` renders plain MUI buttons, whose default height is
        // ~36px — under the 44px a thumb needs. Applied per button because the
        // footer is a row of them, not one control.
        const touchTarget = { minHeight: 44 } as const;

        const actionButtons = [
          {
            label: t("Cancel", { ns: "Global" }),
            handleClick: handleClose,
            buttonProps: {
              variant: "text" as const,
              disabled: isSubmitting,
              sx: touchTarget,
            },
          },
          ...(isUpdate
            ? []
            : [
                {
                  label: t("Save as draft", { ns: "Templates" }),
                  handleClick: submitWith("draft"),
                  buttonProps: {
                    variant: "outlined" as const,
                    loading:
                      isSubmitting && submitActionRef.current === "draft",
                    disabled: cannotSave,
                    sx: touchTarget,
                  },
                },
              ]),
          {
            label: isUpdate
              ? t("Update and publish", { ns: "Templates" })
              : t("Save & Publish…", { ns: "Templates" }),
            handleClick: submitWith("publish"),
            buttonProps: {
              loading: isSubmitting && submitActionRef.current === "publish",
              disabled: cannotSave,
              sx: touchTarget,
            },
          },
        ];

        return (
          <Modal
            title={
              isUpdate
                ? t("Update quote template", { ns: "Templates" })
                : t("Save as quote template", { ns: "QuoteItem" })
            }
            // Same size as the other metadata forms (`CreateQuoteTemplateModal`).
            // Deliberately not `fixedHeight` — that forces height: 90%, which is
            // for browse surfaces like `TemplateSelectModal`, not a form.
            maxWidth="sm"
            // Deliberately *not* `isLoading`: `ModalContent` swaps the whole body
            // for a spinner, so the dialog would open at spinner height and jump
            // to full height when the queries land. The structure is mounted from
            // the first frame instead, with the spinner confined to the (fixed
            // height) tab panel and the fields inert until the prefill arrives.
            // Without this the modal-stack registration effect renders the
            // dialog display:none for one frame, which silently defeats MUI's
            // one-shot auto-focus — Escape then does nothing until the user
            // clicks inside. Its children (confirm, strategy modal) stack over.
            notInStack
            // Dismissing mid-flight would abandon a running mutation.
            handleClose={isSubmitting ? undefined : handleClose}
            // The body scrolls as one (`DialogContent`'s default), so the info
            // note cannot live inside it — it would scroll out of sight, which
            // is how it went missing. Pinning it here, in the actions area, puts
            // it directly above the buttons and outside the scroll region.
            // Taking over `dialogActions` means rendering `ModalActions`
            // ourselves; it is exported for exactly this.
            dialogActions={
              <Box
                sx={{
                  flexShrink: 0,
                  // Three actions do not fit a phone in one row, so below the
                  // tablet breakpoint they stack full width — reversed, which
                  // puts the primary action nearest the thumb and Cancel
                  // furthest from an accidental tap. MUI spaces `DialogActions`
                  // children with a left margin that has to go with the row.
                  "& .MuiDialogActions-root": {
                    flexDirection: { xs: "column-reverse", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 1,
                    "& > :not(style) ~ :not(style)": { ml: { xs: 0, sm: 1 } },
                  },
                }}
              >
                {/* Create mode only: the note's advice is to save a draft and
                    clean it up later, and update mode has no draft path.
                    Mirrors the theme's `MuiDialogContent` horizontal padding
                    (8px below md, 12px above — not MUI's default 24px) so the
                    note lines up with the body it belongs to. */}
                {!isUpdate && (
                  <Alert
                    severity="info"
                    sx={{ mx: { xs: 1, md: 1.5 }, mt: 1.5, mb: 1 }}
                  >
                    {t(
                      "Remove client-specific details before publishing. To clean it up later, save the template as a draft.",
                      { ns: "Templates" }
                    )}
                  </Alert>
                )}
                <ModalActions actionButtons={actionButtons} />
              </Box>
            }
          >
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {isUpdate
                  ? t(
                      'From "{source}" · {count, plural, one {# item} other {# items}} · Updates "{target}"',
                      {
                        ns: "Templates",
                        source: sourceTitle,
                        count: sourceItem ? countRows(sourceItem.children) : 0,
                        target: targetTemplate?.title ?? "",
                      }
                    )
                  : t(
                      'From "{source}" · {count, plural, one {# item} other {# items}}',
                      {
                        ns: "Templates",
                        source: sourceTitle,
                        count: sourceItem ? countRows(sourceItem.children) : 0,
                      }
                    )}
              </Typography>

              {targetTemplate?.containsUnpublishedChanged && (
                <Alert severity="warning">
                  {t(
                    "This template has unpublished draft changes. Updating replaces them.",
                    { ns: "Templates" }
                  )}
                </Alert>
              )}

              {/* Said outright, because every field below is empty and inert:
                  without this the dialog reads as "this section has nothing in
                  it" rather than "we could not read it". */}
              {hasUnavailableData && (
                <ErrorMessage
                  message={
                    isUpdate
                      ? t(
                          "This template could not be loaded, so it cannot be updated. Close the dialog and try again.",
                          { ns: "Templates" }
                        )
                      : t(
                          "This section could not be loaded, so it cannot be saved as a template. Close the dialog and try again.",
                          { ns: "Templates" }
                        )
                  }
                />
              )}

              {validationError && <ErrorMessage message={validationError} />}

              <Form id={formId}>
                <Stack direction="column" spacing={1}>
                  <TextField
                    label={t("Template name", { ns: "Templates" })}
                    name="title"
                    required
                    disabled={fieldsDisabled}
                    autoFocus
                  />

                  {/* The type is inherited, so it is stated rather than picked:
                      no search modal, and with it no properties-strategy
                      question — changing a template's type belongs to the
                      template editor (`TemplatesQuoteTemplateSettingsModal`),
                      which acts on a template that already exists.
                      Rendered from the value the dialog already holds instead of
                      through `TemplateTypeInput canEdit={false}`, which would
                      re-query the title it was given and take a callback it
                      cannot use.
                      Omitted entirely when nothing is inherited: a locked empty
                      field invites a click and answers with nothing.
                      Category still delegates to its search modal, with
                      `alwaysShowLabel` so the block reads as one set of labelled
                      fields. Both are wrapped in `<CompactLabeledField>` to drop
                      the 48px row floor those inputs apply — locally, so the five
                      other screens using `CategoryInput alwaysShowLabel` keep
                      theirs. */}
                  {formikProps.values.templateType && (
                    <CompactLabeledField>
                      <LabeledValue
                        label={t("Template type", { ns: "Templates" })}
                      >
                        {formikProps.values.templateType.title}
                      </LabeledValue>
                    </CompactLabeledField>
                  )}

                  <CompactLabeledField>
                    <CategoryInput
                      categoryId={formikProps.values.category}
                      label={t("Template category", { ns: "Templates" })}
                      selectButtonLabel={t("Add template category", {
                        ns: "Templates",
                      })}
                      alwaysShowLabel
                      disabled={fieldsDisabled}
                      onChange={async category => {
                        await formikProps.setFieldValue(
                          "category",
                          category.id
                        );
                      }}
                      onClear={
                        formikProps.values.category
                          ? async () => {
                              await formikProps.setFieldValue("category", null);
                            }
                          : undefined
                      }
                    />
                  </CompactLabeledField>

                  <SelectMultipleField
                    label={t("Type of project", { ns: "Templates" })}
                    name="typeOfProject"
                    options={projectTypeOptions}
                    disabled={fieldsDisabled}
                    clearable
                  />
                </Stack>
              </Form>

              <Tabs<DialogTab, TabOption<DialogTab>>
                onChange={setTab}
                value={tab}
                options={tabOptions}
              />

              {/* The tab panel container. Content-sized with a floor — the body
                  scrolls as a whole, so the panel does not scroll itself and
                  must not be given a fixed height. */}
              <Box
                sx={{
                  minHeight: TAB_PANEL_MIN_HEIGHT,
                  display: "flex",
                  flexDirection: "column",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  // The Structure list draws its rows edge to edge, so it
                  // supplies the inset itself.
                  p: tab === "structure" ? 0 : 2,
                }}
              >
                {isLoading ? (
                  <LoadingSpinner />
                ) : tab === "preview" ? (
                  <PreviewTab
                    formikProps={formikProps}
                    sourceItem={sourceItem}
                    disabled={isSubmitting}
                  />
                ) : tab === "properties" ? (
                  <PropertiesTab
                    projectId={projectId}
                    docId={docId}
                    sourceItem={sourceItem}
                    properties={displayedProperties}
                    isTemplateTypeDriven={Boolean(templateTypeProps2)}
                    stagedEntries={formikProps.values.props2Entries}
                    disabled={fieldsDisabled}
                    onPropertyChange={async entry => {
                      await formikProps.setFieldValue(
                        "props2Entries",
                        stageProps2Entry(
                          formikProps.values.props2Entries,
                          entry
                        )
                      );
                    }}
                  />
                ) : (
                  <StructureTab
                    sourceItem={sourceItem}
                    childrenVisibility={formikProps.values.childrenVisibility}
                    disabled={isSubmitting}
                    onChildrenVisibilityChange={async childrenVisibility => {
                      await formikProps.setFieldValue(
                        "childrenVisibility",
                        childrenVisibility
                      );
                    }}
                  />
                )}
              </Box>
            </Stack>

            <ConfirmProcess ref={confirmProcessRef} />
          </Modal>
        );
      }}
    </Formik>
  );
};

/**
 * Drops the 48px minimum row height that `TemplateTypeInput` and `CategoryInput`
 * put on their empty state, so those two fields sit as tightly as the rest of
 * the metadata block.
 *
 * Done here rather than in the shared components on purpose: `CategoryInput`'s
 * `alwaysShowLabel` is also used by five other screens (`ProjectCategory`,
 * `ProjectOverviewHeaderBox`, `CraftsmenSelectModal`, `CrmCompanyDetailsBox`,
 * `RequestProjectInfoModal`), where that floor doubles as a comfortable tap
 * target. Only the value row carries a `minHeight`, so unsetting it across the
 * descendant Typographies is safe.
 */
const CompactLabeledField = ({ children }: React.PropsWithChildren) => (
  <Box sx={{ "& .MuiTypography-root": { minHeight: "unset" } }}>{children}</Box>
);

/**
 * How the template will present itself: the picture it inherits from the source
 * item, its name (edited above, shown here read-only so one field keeps one
 * control) and its description, which is edited here and nowhere else.
 */
const PreviewTab = ({
  formikProps,
  sourceItem,
  disabled,
}: {
  formikProps: FormikProps<FormValues>;
  sourceItem: SaveTemplateDialog_SourceItemFragment | undefined;
  disabled: boolean;
}) => {
  const { t } = useTranslate(["QuoteItem", "Templates", "Global"]);

  const picture = getPictures(sourceItem?.attachments ?? [])[0];

  return (
    <Stack direction="column" spacing={1}>
      {/* Same shape as the organisation banner slot
          (`OrganisationDetailsProfilePicturesBox`): one bordered, grey-filled
          box of fixed height holding either the image or a centred icon with a
          caption.
          **Preview only — deliberately no upload affordance.** The template does
          not exist until the mutation runs, so a photo cannot be attached from
          here (design D3a); the picture is attached on the item detail panel and
          this only shows what the template will inherit. Hence
          `ImageNotSupportedIcon`, the repo's read-only marker
          (`AllowedValueLabelWithOverride.tsx:99`), and *not* the `AddAPhotoIcon`
          the organisation banner uses — there, uploading is the point. No click
          handler, no pointer cursor, no overlay button. */}
      <Box
        sx={{
          height: PICTURE_SLOT_HEIGHT,
          flexShrink: 0,
          borderRadius: 1,
          border: theme => `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          bgcolor: "grey.100",
        }}
      >
        {picture ? (
          <Box
            component="img"
            src={picture.url}
            alt={picture.title}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <ImageNotSupportedIcon fontSize="large" color="disabled" />
            <Typography variant="caption">
              {t("No photo", { ns: "Templates" })}
            </Typography>
          </Stack>
        )}
      </Box>

      <LabeledValue label={t("Template name", { ns: "Templates" })}>
        {formikProps.values.title}
      </LabeledValue>

      <RichTextEditor
        label={t("Description", { ns: "QuoteItem" })}
        htmlContent={formikProps.values.description}
        disabled={disabled}
        onChange={async (content: RawDraftContentState) => {
          await formikProps.setFieldValue(
            "description",
            cleanHTML(draftStateToHtml(convertFromRaw(content)))
          );
        }}
        saveButtonLabel={t("Save", { ns: "Global" })}
        cancelButtonLabel={t("Cancel", { ns: "Global" })}
      />
    </Stack>
  );
};

/**
 * The properties the created template will carry, with editable values.
 *
 * The list comes from `deriveSaveTemplateProperties`, not from the source item:
 * a template type placeholder adds no properties to the item it sits on, so the
 * item's own `props2` would report none for a template created with the type's
 * full set.
 */
const PropertiesTab = ({
  projectId,
  docId,
  sourceItem,
  properties: derivedProperties,
  isTemplateTypeDriven,
  stagedEntries,
  disabled,
  onPropertyChange,
}: {
  projectId: string | null;
  docId: string;
  sourceItem: SaveTemplateDialog_SourceItemFragment | undefined;
  properties: readonly Props2AllFragment[];
  /** Whether the list came from a template type, which the empty state names. */
  isTemplateTypeDriven: boolean;
  stagedEntries: EnterItemProps2ValueEntry[];
  disabled: boolean;
  onPropertyChange: (entry: EnterItemProps2ValueEntry) => Promise<void> | void;
}) => {
  const { t } = useTranslate(["QuoteItem", "Templates"]);

  const properties = React.useMemo(
    () => applyStagedProps2(derivedProperties, stagedEntries),
    [derivedProperties, stagedEntries]
  );
  const groups = React.useMemo(
    () => groupProperties([...properties]),
    [properties]
  );

  if (!sourceItem) return null;

  if (properties.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {isTemplateTypeDriven
          ? t("This template type defines no properties", { ns: "Templates" })
          : t("This section has no properties yet", { ns: "Templates" })}
      </Typography>
    );
  }

  return (
    <Stack direction="column" spacing={1}>
      {groups.map(group => (
        <Stack
          key={`properties-group-${group.key ?? "__other__"}`}
          direction="column"
          spacing={1}
        >
          {group.key && <Typography variant="body1">{group.key}</Typography>}
          {group.properties.map(property => (
            <PropertyField
              key={property.key}
              projectId={projectId}
              docId={docId}
              itemId={sourceItem.id}
              property={property}
              // Staged, not written: the edit rides along on the save mutation
              // rather than touching the source quote item.
              setPropertyValue={onPropertyChange}
              // No "Build your formula" button. An expression is authored
              // against the document it lives in, and PropertyExpressionModal
              // writes it through its own mutation — neither of which this
              // staged, not-yet-created template can offer. Expressions stay a
              // template-editor concern.
              //
              // The flag also keeps a computed property — *itself* a button
              // opening the same modal, which would write the source quote
              // item's whole props2 set through `defineItemProps2` — from
              // opening it. Computed rows are still read-only outright because
              // they have no value to stage: their value comes from their
              // expression, and `EnterItemProps2ValueEntry` has no computed
              // variant.
              disableExpressions
              readOnly={disabled || !isNonComputedProp(property)}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
};

/** The property key an `EnterItemProps2ValueEntry` targets, whichever kind it carries. */
export const props2EntryKey = (
  entry: EnterItemProps2ValueEntry
): string | undefined =>
  entry.bool?.key ??
  entry.number?.key ??
  entry.numberArray?.key ??
  entry.text?.key ??
  entry.textArray?.key;

/**
 * Replaces any earlier entry for the same property, so the staged list holds one
 * entry per key and the last edit wins.
 */
export const stageProps2Entry = (
  staged: EnterItemProps2ValueEntry[],
  entry: EnterItemProps2ValueEntry
): EnterItemProps2ValueEntry[] => {
  const key = props2EntryKey(entry);
  return [...staged.filter(e => props2EntryKey(e) !== key), entry];
};

/**
 * Overlays the staged edits onto the listed properties so the fields show what
 * the user typed. Nothing is written to the quote — the staged entries ride along
 * on the mutation instead, which is why the displayed value has to be
 * reconstructed here.
 *
 * Only the non-computed kinds are editable; a computed property's value comes
 * from its expression, so it has no value to stage.
 */
export const applyStagedProps2 = (
  properties: readonly Props2AllFragment[],
  staged: EnterItemProps2ValueEntry[]
): readonly Props2AllFragment[] => {
  if (!staged.length) return properties;

  return properties.map(property => {
    const entry = staged.find(e => props2EntryKey(e) === property.key);
    if (!entry) return property;

    switch (property.__typename) {
      case "Props2Bool":
        return entry.bool
          ? { ...property, valueBool: entry.bool.valueBool ?? null }
          : property;
      case "Props2Number":
        return entry.number
          ? { ...property, valueNumber: entry.number.valueNumber ?? null }
          : property;
      case "Props2NumberArray":
        return entry.numberArray
          ? {
              ...property,
              valueNumberArray: entry.numberArray.valueNumberArray ?? null,
            }
          : property;
      case "Props2Text":
        return entry.text
          ? { ...property, valueText: entry.text.valueText ?? null }
          : property;
      case "Props2TextArray":
        return entry.textArray
          ? {
              ...property,
              valueTextArray: entry.textArray.valueTextArray ?? null,
            }
          : property;
      default:
        return property;
    }
  });
};

/**
 * A shallow tree of what the template will contain: the root item as a header
 * row carrying the subitem-visibility control, its children indented beneath and
 * separated by hairline dividers. The bordered list *is* this tab's panel
 * container, which is why the panel drops its padding for this tab.
 *
 * Rows are separated by dividers rather than tinted: a filled row background has
 * essentially no precedent in this codebase (the only other use of
 * `action.hover` is a hover state inside an autocomplete popper).
 *
 * Read-only apart from the visibility control, whose value is staged into the
 * form rather than written to the source quote — the reason it cannot simply be
 * `ChildVisibilityIconButton`, which mutates the item it is given. Everything
 * else about it (icon set, labels, `MenuButton` + `MenuItemWithIcon`) is that
 * button's shape.
 */
const StructureTab = ({
  sourceItem,
  childrenVisibility,
  disabled,
  onChildrenVisibilityChange,
}: {
  sourceItem: SaveTemplateDialog_SourceItemFragment | undefined;
  childrenVisibility: ItemChildrenVisibility | null;
  disabled: boolean;
  onChildrenVisibilityChange: (
    value: ItemChildrenVisibility
  ) => Promise<void> | void;
}) => {
  const { t } = useTranslate(["QuoteItem", "Templates"]);

  if (!sourceItem) return null;

  const canSetVisibility = canCarryChildrenVisibility(sourceItem);
  const currentVisibility = childrenVisibility ?? "SHOW_CHILDREN";

  const visibilityLabels: Record<ItemChildrenVisibility, string> = {
    SHOW_CHILDREN: t("SHOW_CHILDREN", { ns: "QuoteItem" }),
    SHOW_CHILDREN_HIDE_PRICE: t("SHOW_CHILDREN_HIDE_PRICE", {
      ns: "QuoteItem",
    }),
    SHOW_ONLY_MAINPRODUCT_HIDE_PRICE: t("SHOW_ONLY_MAINPRODUCT_HIDE_PRICE", {
      ns: "QuoteItem",
    }),
    HIDE_CHILDREN: t("HIDE_CHILDREN", { ns: "QuoteItem" }),
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          px: 1.5,
          py: 0.5,
          minHeight: 44,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {sourceItem.title}
        </Typography>
        {canSetVisibility && (
          <MenuButton
            Icon={CHILDREN_VISIBILITY_ICONS[currentVisibility]}
            tooltip={t("Subitem visibility", { ns: "Templates" })}
            disabled={disabled}
            buttonProps={{
              "aria-label": t("Subitem visibility", { ns: "Templates" }),
            }}
          >
            {CHILDREN_VISIBILITY_OPTIONS.map(option => (
              <MenuItemWithIcon
                key={option}
                selected={currentVisibility === option}
                icon={CHILDREN_VISIBILITY_ICONS[option]}
                onClick={() => {
                  void onChildrenVisibilityChange(option);
                }}
              >
                {visibilityLabels[option]}
              </MenuItemWithIcon>
            ))}
          </MenuButton>
        )}
      </Stack>

      {sourceItem.children.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 1.5, py: 1 }}
        >
          {t("No subitems", { ns: "Templates" })}
        </Typography>
      ) : (
        <StructureRows items={sourceItem.children} depth={0} />
      )}
    </Box>
  );
};

/**
 * A sub-tree row and everything under it. Recurses over whatever the query
 * returned, so the visible depth is set by the fragment's nesting rather than by
 * this component — the tab used to show one level because the fragment carried
 * one, not because it rendered one.
 */
const StructureRows = ({
  items,
  depth,
}: {
  items: readonly StructureRowItem[];
  depth: number;
}) => (
  <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
    {items.map((item, index) => (
      <Box
        component="li"
        key={item.id}
        sx={{
          // Rules separate top-level groups, and nothing else. A rule on the row
          // itself cannot work here: a nested list renders inside its parent's
          // <li>, straight after the parent's row, so a border under a row that
          // has children lands between that row and its own first child —
          // cutting the parent off from its sub-tree and visually attaching the
          // sub-tree's last row to the parent's next sibling. Bordering the top
          // of each subsequent depth-0 <li> instead keeps every section whole.
          ...(depth === 0 &&
            index > 0 && {
              borderTop: 1,
              borderColor: "divider",
            }),
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            // Steps in per level, starting where the header row's title sits.
            pl: 3.75 + depth * 2,
            pr: 1.5,
            py: 0.75,
          }}
        >
          {item.title}
        </Typography>
        {item.children && item.children.length > 0 && (
          <StructureRows items={item.children} depth={depth + 1} />
        )}
      </Box>
    ))}
  </Box>
);

/**
 * `aggregate_itemSetChildrenVisibility` throws for any other item type, and the
 * save applies the staged value to the created template's root unconditionally
 * once the field is present — so an item that cannot carry a visibility must
 * send none at all, not the value it happens to report.
 */
const canCarryChildrenVisibility = (
  item: { type: ItemType } | undefined
): boolean => item?.type === "section" || item?.type === "decision";
